/**
 * useProfileInfo - Hook for fetching user profile information
 */

import { useCallback, useEffect, useState } from "react";
import { useApi } from "./useApi";
import { getUserByAddress } from "../api/services/userService";

/**
 * Hook to fetch user profile information by wallet address
 * @param {string} userAddress - User's wallet address
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Fetch immediately on mount (default: false)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @returns {Object} - { profileData, loading, error, refetch }
 */
export const useProfileInfo = (userAddress, options = {}) => {
  const { immediate = false, onSuccess, onError } = options;
  const [shouldFetch, setShouldFetch] = useState(false);

  const fetchProfile = useCallback(
    () => getUserByAddress(userAddress),
    [userAddress],
  );

  const { data, loading, error, execute, reset } = useApi(fetchProfile, {
    immediate: false, // Don't use useApi's immediate, we'll control it
    initialData: null,
    onSuccess,
    onError,
  });

  // Trigger fetch when address becomes available, reset when address is cleared
  useEffect(() => {
    if (userAddress && (immediate || shouldFetch)) {
      execute();
    } else if (!userAddress) {
      // Reset data when wallet disconnects
      reset();
    }
  }, [userAddress, immediate, shouldFetch, execute, reset]);

  // Extract profile data with defaults
  const profileData = data
    ? {
        twitterName: data.twitterName || "User",
        twitterUsername: data.twitterUsername || "User",
        twitterProfilePictureUrl: data.twitterProfilePictureUrl || null,
        isTwitterConnected: data.isTwitterConnected || false,
        isDiscordConnected: data.isDiscordConnected || false,
        discordUsername: data.discordUsername || null,
        discordGlobalName: data.discordGlobalName || null,
        discordProfilePictureUrl: data.discordProfilePictureUrl || null,
        discordGuildCount: data.discordGuildCount || null,
        isTelegramConnected: data.isTelegramConnected || false,
        telegramUsername: data.telegramUsername || null,
        telegramFirstName: data.telegramFirstName || null,
        telegramProfilePictureUrl: data.telegramProfilePictureUrl || null,
        mindshare: data.mindshare || 0,
        multiplier: data.multiplier || 1,
        xpBoosts: data.xpBoosts || [],
        // Effective rate on newly-earned XP from active boost windows.
        // Boosts are additive on the bonus, so factors combine as 1 + Σ(f-1).
        xpBoostFactor: (data.xpBoosts || [])
          .filter((b) => b.endAt == null || b.endAt > Date.now())
          .reduce((acc, b) => acc + ((b.factor || 1) - 1), 1),
        twitterFollowers: data.twitterFollowers || 0,
        rank: data.rank || "N/A",
        score: data.score || 0,
        tier: data.tier || "Bronze",
        address: data.address,
      }
    : null;

  return {
    profileData,
    loading,
    error,
    refetch: execute,
    reset,
  };
};

export default useProfileInfo;
